import React from "react";
import { withRouter } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import api from "../../api/api";
import Header from "../../components/Header";
import withRecoil from "../../withRecoil";
import { accessTokenState } from "../../atoms";
import { Modal, Spin } from 'antd';

// Initialize Stripe outside the class render loop using your publishable key
const stripePromise = loadStripe(api.stripe_pub_key);

class FundAccountStripe extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            amount: "10",
            feeSchedule: null,
            fee: 0,
            total: 0,
            errorMessage: "",
            clientSecret: null, // Replaces orderId for Stripe Embedded Session integration
            isLoading: true
        };
    }

    componentDidMount() {
        this.fetchFeeSchedule();
    }

    fetchFeeSchedule = async () => {
        try {
            if (!this.props.recoilStates.accessTokenState.state) {
                throw new Error("Access token is missing");
            }

            const amount = this.state.amount || "10";
            const accessToken = this.props.recoilStates.accessTokenState.state;
            const url = `${api.main}/get_fee_schedule?amount=${encodeURIComponent(amount)}&access_token=${encodeURIComponent(accessToken)}`;

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Accept": "application/json"
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();

            // Modifying code slightly to fetch your Stripe setup instead of PayPal
            if (data.status === 105 && data.data && data.data.length > 0) {
                const stripeFeeSchedules = data.data.filter(schedule => schedule.source === "Stripe");

                if (stripeFeeSchedules.length > 0) {
                    this.setState({
                        feeSchedule: stripeFeeSchedules
                    }, () => {
                        this.calculateStripeFee();
                    });
                } else {
                    console.error("No fee schedules found.");
                    this.setState({ feeSchedule: [] });
                }
            } else {
                this.setState({ feeSchedule: [] });
            }

        } catch (error) {
            console.error('Failed to fetch fee schedule:', error);
            this.setState({ isLoading: false }, () => {
                this.calculateStripeFee();
            });
        }
    };

    handleAmountChange = (event) => {
        const newAmount = event.target.value;

        if (parseFloat(newAmount) < 10) {
            this.setState({
                amount: newAmount,
                errorMessage: "Please input an amount of $10 or greater.",
                clientSecret: null // Wipe the checkout container if they lower the amount
            });
        } else {
            this.setState({
                amount: newAmount,
                errorMessage: "",
                isLoading: true,
                clientSecret: null // Wipe outdated checkout instance until new checkout request is invoked
            }, () => {
                this.fetchFeeSchedule();
            });
        }
    };

    // Swapped from createOrder to target your Stripe checkout creation endpoint
    createCheckoutSession = async () => {
        try {
            if (parseFloat(this.state.amount) < 10) {
                alert("Please enter an amount of $10 or greater.");
                return;
            }

            const accessToken = this.props.recoilStates.accessTokenState.state;
            if (!accessToken) {
                throw new Error("Access token is missing");
            }

            // Convert total transaction value strictly to numerical integer CENTS
            //const totalInCents = Math.round(parseFloat(this.state.total) * 100);

            this.setState({ isLoading: true });

            const response = await fetch(`${api.extra}/api/deposits/create-checkout-session`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    amount: parseFloat(this.state.amount), // Amount required in cents format
                    fee: parseFloat(this.state.fee), // Amount required in cents format
                    total: parseFloat(this.state.total), // Amount required in cents format
                    access_token: accessToken
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || `HTTP error! Status: ${response.status}`);
            }

            if (!data.clientSecret) {
                throw new Error("Client Secret missing from response configuration layout");
            }

            this.setState({
                clientSecret: data.clientSecret,
                isLoading: false
            });
        } catch (error) {
            alert(error.message || "Failed to launch Stripe checkout. Please try again.");
            this.setState({ isLoading: false });
        }
    };

    calculateStripeFee = async () => {
        const amount = parseFloat(this.state.amount);

        if (isNaN(amount) || amount < 10) {
            this.setState({ fee: 0, total: 0, isLoading: false });
            return;
        }

        if (!this.state.feeSchedule || !Array.isArray(this.state.feeSchedule)) {
            console.error("Fee schedule is invalid");
            return;
        }

        const applicableFee = this.state.feeSchedule.find(schedule => {
            const start = parseFloat(schedule.amount_start);
            const end = schedule.amount_end !== null ? parseFloat(schedule.amount_end) : Infinity;

            return amount >= start && amount <= end;
        });

        if (applicableFee) {
            const fee = applicableFee.flat_rate + (amount * applicableFee.percentage / 100);
            const total = amount + fee;

            await this.setState({
                fee,
                total,
                isLoading: false
            });
        }
    };

    render() {
        return (
            <React.Fragment>
                <Header />
                <div className="spare-dvs" style={{ minHeight: "calc(100vh - 80px)" }}>
                    <div style={{ maxWidth: '600px', margin: '0 auto', padding: '10px' }}>

                        {/* Back Button + Centered Header */}
                        <div style={{ position: 'relative', marginBottom: '20px', textAlign: 'center', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <button
                                onClick={() => this.props.history.push('/add-to-balance')}
                                style={{
                                    border: 'none',
                                    background: 'none',
                                    cursor: 'pointer',
                                    minWidth: '25px'
                                }}
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M15 19l-7-7 7-7" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <line x1="8" y1="12" x2="20" y2="12" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                            <h2 style={{ margin: '0' }}>Add to Balance - Stripe</h2>
                        </div>

                        {/* Amount Entry View Form block - Hidden once Stripe takes over frame */}
                        {!this.state.clientSecret && (
                            <div style={{ marginBottom: "25px", textAlign: "center" }}>
                                <label style={{ fontSize: "18px" }}>
                                    Amount: $
                                    <select
                                        value={this.state.amount}
                                        onChange={this.handleAmountChange}
                                        style={{
                                            marginLeft: "10px",
                                            padding: "8px 12px",
                                            fontSize: "16px",
                                            borderRadius: "5px",
                                            border: "1px solid #ccc",
                                            background: "white",
                                            cursor: "pointer",
                                            width: "140px",
                                            textAlignLast: "center"
                                        }}
                                    >
                                        <option key="10" value="10">
                                            $10
                                        </option>
                                        <option key="20" value="20">
                                            $20
                                        </option>
                                        <option key="50" value="50">
                                            $50
                                        </option>
                                        <option key="100" value="100">
                                            $100
                                        </option>
                                        <option key="200" value="200">
                                            $200
                                        </option>
                                        <option key="500" value="500">
                                            $500
                                        </option>
                                    </select>
                                </label>
                                {this.state.errorMessage && (
                                    <div style={{ color: "red", fontSize: "14px", marginTop: "5px" }}>
                                        {this.state.errorMessage}
                                    </div>
                                )}
                                <p style={{ color: "orange", margin: "10px 0 5px" }}>
                                    Transaction fee: ${this.state.fee.toFixed(2)}
                                </p>
                                <p style={{ color: "black", fontWeight: "bold" }}>
                                    Total: ${this.state.total.toFixed(2)}
                                </p>

                                <button
                                    onClick={this.createCheckoutSession}
                                    disabled={this.state.isLoading || parseFloat(this.state.amount) < 10}
                                    style={{
                                        margin: "15px auto 0 auto",
                                        padding: "10px 30px",
                                        fontSize: "16px",
                                        backgroundColor: "#635bff",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "5px",
                                        cursor: "pointer",
                                        opacity: (this.state.isLoading || parseFloat(this.state.amount) < 10) ? 0.6 : 1
                                    }}
                                >
                                    Proceed to Payment
                                </button>
                            </div>
                        )}

                        {/* Dynamic Render: Loading Spin Indicator vs Embedded Form */}
                        <div style={{ maxWidth: "500px", margin: "0 auto" }}>
                            {this.state.isLoading ? (
                                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "120px" }}>
                                    <Spin size="large" tip="Processing details..." />
                                </div>
                            ) : (
                                this.state.clientSecret && (
                                    <div>
                                        <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret: this.state.clientSecret }}>
                                            <EmbeddedCheckout />
                                        </EmbeddedCheckoutProvider>

                                        <button
                                            onClick={() => this.setState({ clientSecret: null })}
                                            style={{ marginTop: '20px', background: 'none', border: 'none', color: '#635bff', cursor: 'pointer' }}
                                        >
                                            ← Change Deposit Amount
                                        </button>
                                    </div>
                                )
                            )}
                        </div>

                    </div>
                </div>
            </React.Fragment>
        );
    }
}

export default withRecoil(withRouter(FundAccountStripe), [accessTokenState]);